/**
 * Web Push subscription — shared by the ERP shell and the customer portal.
 *
 * `client` and `base` default to the ERP pair, so every pre-existing caller is
 * unchanged. The portal passes its own, for the same reason the chat API is
 * mounted twice: a `user_type='CUSTOMER'` token is confined to the
 * customer-facing prefixes at the edge, so `/api/push/` would 401 for a
 * customer contact no matter what they had been admitted to. Same endpoints,
 * two front doors — see `apps/customer_portal/api/urls.py`.
 */
import axios from './api';

const ERP_BASE = '/api/push';

async function getVapidPublicKey(client, base) {
  const { data } = await client.get(`${base}/vapid-key/`);
  return data.publicKey;
}

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64  = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw     = atob(base64);
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

export async function subscribeToPush({ client = axios, base = ERP_BASE } = {}) {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) return null;

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return null;

  const vapidKey = await getVapidPublicKey(client, base);
  if (!vapidKey) return null;

  const registration = await navigator.serviceWorker.ready;
  let subscription = await registration.pushManager.getSubscription();

  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidKey),
    });
  }

  const subJson = subscription.toJSON();
  await client.post(`${base}/subscribe/`, {
    endpoint:   subJson.endpoint,
    keys:       subJson.keys,
    user_agent: navigator.userAgent,
  });

  return subscription;
}

export async function unsubscribeFromPush({ client = axios, base = ERP_BASE } = {}) {
  if (!('serviceWorker' in navigator)) return;

  const registration = await navigator.serviceWorker.ready;
  const subscription = await registration.pushManager.getSubscription();
  if (!subscription) return;

  await client.post(`${base}/unsubscribe/`, { endpoint: subscription.endpoint });
  await subscription.unsubscribe();
}
